<script setup lang="ts">
/**
 * 应用程序根组件
 * 负责管理全局布局、页面加载状态和路由变化处理
 */

// 导入Vue核心API
import { nextTick, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";

// 导入公共组件
import Footer from "./components/Footer.vue"; // 页面底部组件
import Header from "./components/Header.vue"; // 页面头部组件
import PageLoading from "./components/common/PageLoading.vue"; // 页面加载动画组件

// 导入工具函数
import { destroyScreenDetector, initScreenDetector } from "./utils/screen-detector"; // 屏幕分辨率检测器

// 响应式数据：页面加载状态（默认显示加载动画）
const isLoading = ref(true);

// 获取路由实例
const router = useRouter();

/**
 * 路由守卫：路由切换前显示加载动画
 */
router.beforeEach(() => {
  isLoading.value = true;
});

/**
 * 路由守卫：路由切换完成后隐藏加载动画
 */
router.afterEach(() => {
  // 使用nextTick确保DOM更新完成
  nextTick(() => {
    // 延迟600ms隐藏加载动画，确保页面内容和样式完全渲染
    setTimeout(() => {
      isLoading.value = false;
    }, 600);
  });
});

/**
 * 组件挂载时的初始化逻辑
 */
onMounted(() => {
  // 开发环境下初始化屏幕分辨率检测器
  if (import.meta.env.DEV) {
    initScreenDetector();
  }

  // 确保Vue实例完全初始化
  nextTick(() => {
    /**
     * 处理页面初始加载动画
     * 等待所有资源加载完成后再隐藏加载动画
     */
    if (typeof window !== "undefined" && document.readyState === "complete") {
      // 页面已加载完成，等待400ms确保样式渲染
      setTimeout(() => {
        isLoading.value = false;
      }, 400);
    } else if (typeof window !== "undefined") {
      // 监听页面完全加载事件
      window.addEventListener("load", onWindowLoad);

      // 设置最大等待时间2.5秒，避免加载动画无限显示
      setTimeout(() => {
        isLoading.value = false;
      }, 2500);
    }
  });
});

/**
 * 窗口加载完成事件处理函数
 */
const onWindowLoad = () => {
  // 资源加载完成后等待400ms确保样式渲染
  setTimeout(() => {
    isLoading.value = false;
  }, 400);
};

/**
 * 组件卸载时的清理逻辑
 */
onUnmounted(() => {
  // 移除窗口加载事件监听器
  if (typeof window !== "undefined") {
    window.removeEventListener("load", onWindowLoad);
  }

  // 开发环境下清理屏幕分辨率检测器
  if (import.meta.env.DEV) {
    destroyScreenDetector();
  }
});
</script>

<template>
  <!-- 添加 v-cloak 指令防止页面加载时闪烁 -->
  <div id="app" v-cloak>
    <!-- 页面加载过渡动画 -->
    <PageLoading
      :loading="isLoading"
      title="联合毕设平台"
      description="正在为您加载精彩内容..."
      :show-progress="true"
      :duration="2000"
    />

    <!-- 页面内容：加载完成前隐藏 -->
    <div :class="{ 'hidden-content': isLoading }">
      <!-- 使用公共头部组件 -->
      <Header />

      <main class="main-content">
        <router-view />
      </main>

      <!-- 使用公共底部组件 -->
      <Footer />
    </div>
  </div>
</template>

<style scoped>
/* 加载完成前隐藏页面内容 */
.hidden-content {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}
</style>
